/**
 * BatchDetailScreen Component
 *
 * Displays batch details with label and picklist printing (DTG only in V1).
 * Uses hidden print templates that are copied to print window.
 *
 * Review Feedback Applied:
 * - Picking mode from settings context (not hardcoded)
 * - ThreadHeads .empty-state pattern for error states
 * - Back button with text label for accessibility
 * - Responsive print buttons (flex-col sm:flex-row)
 * - Table with overflow-x-auto for mobile
 * - aria-labels on interactive elements
 */

import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Printer,
  FileText,
  Check,
  AlertCircle,
  Package,
  LayoutGrid,
  Loader2,
  ChevronDown,
  RotateCcw,
  ExternalLink,
  ArrowUpCircle,
  Receipt,
  Film,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useBatchLabels, useLogPrintEvent, useUpdateBatchStatus, useReportPickingIssue, useBatch, useCompletePickingPhase, useBuyList, useSyncBuyList } from '../hooks/useBatches';
import { BuyFirstCard, BuyFirstLoadError, BuyFirstMenu, BuyFirstPrintSection } from './BuyFirstSection';
import { BUY_FIRST_PRINT_CSS, hasBuyFirstContent } from '../utils/buy-first';
import { useRole } from '@/hooks/use-role';
import { isCoordinatorRole } from '@/lib/roles';
import { jitPoErrorKey } from '@/lib/jit-po-errors';
import { useDefaultLabelSize } from '../hooks/useLabelTemplate';
import { BatchProgressBar } from './BatchProgressBar';
import { BatchStageCards } from './BatchStageCards';
import { usePrintLabels } from '../hooks/usePrintLabels';
import { GarmentLabel } from './labels/GarmentLabel';
import { LabelErrorBoundary } from './labels/LabelErrorBoundary';
import { getLabelsInPrintOrder } from '../utils/label-sorting';
import { usePickingMode } from '../hooks/useProductionSettings';
import { useProductionSettings } from '@/features/production-settings/hooks/useProductionSettings';
import {
  groupPicklist,
  buildPicklistGroupKey,
  picklistDisplayName,
  isBackupGroup,
  type GroupableGarment,
  type PicklistGroup,
} from '../utils/picklist-grouping';
import { DEFAULT_PICKLIST_SORT, type PicklistSortConfig } from '@threadheads/shared';
import { ReprintConfirmDialog } from './ReprintConfirmDialog';
import { usePrintInvoices } from '../hooks/usePrintInvoices';
import { useInvoiceBatchData } from '../hooks/useInvoiceData';
import { useLocationInvoiceEnabled } from '../hooks/useLocationInvoiceEnabled';
import { OrderInvoice } from './invoices/OrderInvoice';
import { ReprintInvoicesDialog } from './invoices/ReprintInvoicesDialog';
import { PickingIssueDialog, type SkuOption as PickingIssueSkuOption } from './PickingIssueDialog';
import { PickingCompleteDialog } from './PickingCompleteDialog';
import { PickProgressHeader } from './PickProgressHeader';
import { PickingScannerSection } from './PickingScannerSection';
import { PicklistRow, PicklistPickImage } from './PicklistRow';
import { ClientLabelledBadge } from '@/components/production/ClientLabelledBadge';
import { clientLabelledLabel } from '@/components/production/clientLabelledLabel';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { usePickProgress, useVerifyPick, useVerifyPickGroup, useLocationPickingMode, buildVerifyPickInput } from '../hooks/usePicking';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { getShopifyStoreSlug } from '@/lib/shopify-urls';
import { thumbnailUrl } from '@/lib/image-thumbnail';
import { trpc } from '@/lib/trpc';

interface PicklistBand {
  label: string;
  count: number;
}

/**
 * Band starts for `config.bandByFirstKey`: group index → band label + item
 * count. A band starts whenever the COMPOSITE (fulfillmentType, sectionKey)
 * pair changes — keying on sectionKey alone would drop the band at the tier
 * boundary whenever the P&P block opens with the same value the POD block
 * ended on.
 *
 * ⚠ KEEP-IN-LOCKSTEP consumers: the SCREEN band row (Tailwind) and the PRINT
 * band row (inline styles — usePrintLabels copies innerHTML into a fresh
 * document with no Tailwind). Both render from this one computation.
 */
function computeBandStarts(
  groups: PicklistGroup[],
  config: PicklistSortConfig
): Map<number, PicklistBand> {
  const bands = new Map<number, PicklistBand>();
  if (!config.bandByFirstKey || config.keys.length === 0) return bands;
  let startIdx = -1;
  for (let i = 0; i < groups.length; i++) {
    const group = groups[i];
    if (!group) continue;
    const prev = i > 0 ? groups[i - 1] : null;
    const isStart =
      !prev ||
      group.fulfillmentType !== prev.fulfillmentType ||
      group.sectionKey !== prev.sectionKey;
    if (isStart) {
      startIdx = i;
      bands.set(i, { label: group.sectionLabel, count: group.quantity });
    } else if (startIdx >= 0) {
      const band = bands.get(startIdx);
      if (band) band.count += group.quantity;
    }
  }
  return bands;
}

/**
 * The A4 sheet both A4 prints share (the pick list and "Print buy list"). The
 * @page here overrides usePrintLabels' thermal-label @page via the cascade.
 * The wrapping .label div inherits page-break-inside: avoid from
 * usePrintLabels' base styles; unsatisfiable on a multi-page table, some
 * engines push the whole table to page 2, so it is released here.
 */
const A4_SHEET_CSS = `
  @page { size: A4; margin: 20mm; }
  html { color-scheme: light only; }
  body { font-family: "PingFang SC", "Microsoft YaHei", "Noto Sans SC", Arial, sans-serif; color: #000; background: #fff; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #000; padding: 8px; text-align: left; color: #000; }
  th { background: #f0f0f0; color: #000; }
  @media print {
    .label { page-break-inside: auto; break-inside: auto; }
    thead { display: table-header-group; }
  }
`;

/** Picking-sheet section of a group: POD splits by print tech (DTG/DTF); PAP
 *  is one section. Drives the three colour-banded section headers. */
function sectionOfGroup(group: PicklistGroup): 'DTG' | 'DTF' | 'PAP' {
  if (group.fulfillmentType === 'P&P') return 'PAP';
  return group.printTechnology === 'DTF' ? 'DTF' : 'DTG';
}

export function BatchDetailScreen() {
  const { t } = useTranslation('production');
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();

  // State for dialogs
  const [showReprintDialog, setShowReprintDialog] = useState(false);
  const [showReprintInvoicesDialog, setShowReprintInvoicesDialog] = useState(false);
  const [showInventoryErrorDialog, setShowInventoryErrorDialog] = useState(false);
  const [showPickingCompleteDialog, setShowPickingCompleteDialog] = useState(false);

  // Invoice printing state (BLU-328)
  const [invoiceFetchEnabled, setInvoiceFetchEnabled] = useState(false);
  const pickingCompleteShownRef = useRef(false);

  // Local checkbox state for picklist (visual aid before labels are printed)
  const [localCheckedGroups, setLocalCheckedGroups] = useState<Set<string>>(new Set());
  const [progressExpanded, setProgressExpanded] = useState(false);

  // Verify pick group mutation (bulk mark garments as PICKED)
  const verifyGroupMutation = useVerifyPickGroup(batchId || '');

  // Get picking mode from settings (not hardcoded)
  const { pickingMode } = usePickingMode();

  // Get picking verification mode and progress
  const { selectedLocation } = useLocation();
  const { organization } = useOrganization();
  const { data: pickingVerificationMode } = useLocationPickingMode();
  const { data: pickProgress } = usePickProgress(batchId);
  const verifyPickMutation = useVerifyPick(batchId || '');

  const isVerificationRequired = pickingVerificationMode === 'required';
  const { settings: prodSettings } = useProductionSettings();

  // BLU-747: the on-screen picklist carries a leading checkbox column only in
  // 'required' (scan-to-pick) mode. In 'none' mode it is a read-only pick sheet,
  // so the table is one column narrower and the section bands must span 5, not 6.
  // NOTE: the PRINTED picklist below is a separate table that always keeps its
  // paper "Pick" tick column — its colSpan stays hardcoded at 6.
  const picklistColSpan = isVerificationRequired ? 6 : 5;

  // Get org's configured label size for print @page CSS
  const { defaultLabelSize, isLoading: labelSizeLoading, error: labelSizeError } = useDefaultLabelSize();

  // The label print sheet mounts only while printing (see labelPrintPhase), so
  // nothing else on this screen keeps the template list warm. Subscribing here
  // (same query key GarmentLabel's useLabelTemplate reads) means every label
  // mounts with the org's template already in cache. A label that mounted while
  // the list was still loading would render, and print, the legacy layout.
  const { isLoading: labelTemplatesLoading } = trpc.labelTemplates.list.useQuery();

  // Get product type aliases for picklist display
  const { data: aliasData } = trpc.productionSettings.getProductTypeAliases.useQuery();
  const productTypeAliases = aliasData?.aliases;

  // Warn if label size settings failed to load (still allow printing with fallback)
  useEffect(() => {
    if (labelSizeError) {
      toast.warning(t('batch.detail.toastLabelSizeWarning'), {
        description: t('batch.detail.toastLabelSizeWarningDesc'),
      });
    }
  }, [labelSizeError]);

  // Reset picking complete dialog state when navigating between batches
  useEffect(() => {
    pickingCompleteShownRef.current = false;
    setShowPickingCompleteDialog(false);
  }, [batchId]);

  // Load local pick checks from localStorage on mount/batch change
  useEffect(() => {
    if (!batchId) return;
    const stored = localStorage.getItem(`pickChecks-v2-${batchId}`);
    if (stored) {
      try { setLocalCheckedGroups(new Set(JSON.parse(stored))); } catch { /* ignore corrupt data */ }
    } else {
      setLocalCheckedGroups(new Set());
    }
  }, [batchId]);

  // Persist local pick checks to localStorage
  useEffect(() => {
    if (!batchId) return;
    localStorage.setItem(`pickChecks-v2-${batchId}`, JSON.stringify([...localCheckedGroups]));
  }, [batchId, localCheckedGroups]);

  // Fetch batch labels data (for printing)
  const { data: batchData, isLoading, error } = useBatchLabels(batchId);

  // Fetch batch with progress for progress visualization
  // M-5: Capture error to prevent handover dialog from showing on query failure
  const { data: batchWithProgress, error: batchError } = useBatch(batchId, { includeProgress: true });

  // BLU-502: per-location picklist sort config. Guarded — batchData is
  // undefined on first render, so the query only fires once locationId is
  // known. NOTE the scope asymmetry: getPicklistSortConfig is
  // location-scoped while getBatchLabels is org-scoped, so a station user
  // without a profile_locations row for the batch's location reaches this
  // screen but 403s here — that path must fall back loudly (toast below),
  // never silently.
  const {
    data: sortConfigData,
    isLoading: sortLoading,
    isError: sortError,
  } = trpc.productionSettings.getPicklistSortConfig.useQuery(
    { locationId: batchData?.locationId ?? '' },
    { enabled: !!batchData?.locationId }
  );
  const sortConfig = sortConfigData ?? DEFAULT_PICKLIST_SORT;

  // Announce the default-order fallback when the config fails to load
  // (mirrors the labelSizeError warning above). Printing stays disabled on
  // sortError via the print guards — the screen may show the default order,
  // but a sheet must never PRINT in an order the admin did not configure.
  useEffect(() => {
    if (sortError) {
      toast.warning(t('batch.detail.toastSortConfigError'), {
        description: t('batch.detail.toastSortConfigErrorDesc'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortError]);

  // "Buy first": the batch's just-in-time POs. One query, in parallel with
  // the labels; empty for a batch with no just-in-time blank.
  const {
    data: buyList,
    isLoading: buyListLoading,
    isError: buyListError,
    isRefetching: buyListRefetching,
    refetch: refetchBuyList,
  } = useBuyList(batchId);
  const syncBuyList = useSyncBuyList();
  const { can, role } = useRole();
  const hasJustInTimePos = (buyList?.purchaseOrders.length ?? 0) > 0;

  const logPrintEvent = useLogPrintEvent();
  const updateBatchStatus = useUpdateBatchStatus();
  const reportPickingIssue = useReportPickingIssue();
  const completePickingPhase = useCompletePickingPhase();

  // Navigate back: pre-print batches go to Pre-Print page, customer batches go to Picker
  const isPrePrintBatch = (batchData as Record<string, unknown>)?.batch_type === 'pre_print'
    || (batchWithProgress as Record<string, unknown>)?.batch_type === 'pre_print';
  const backUrl = isPrePrintBatch ? '/production/pre-print' : '/production/picker';

  // Print hooks for labels and picklist
  const {
    printRef: labelPrintRef,
    printLabels,
    isPrinting: isPrintingLabels,
  } = usePrintLabels({
    onPrintComplete: () => {
      if (batchId && batchData) {
        // A whole-batch reprint has no reason picker, but the server requires a
        // reason for LABEL_REPRINT; without one the event was refused and the
        // reprint left no audit row. 'other' keeps the trail.
        logPrintEvent.mutate(
          batchData.labelsPrinted
            ? { batchId, printType: 'LABEL_REPRINT', reason: 'other', notes: 'Whole batch reprint' }
            : { batchId, printType: 'LABELS_BATCH' },
        );

        // Transition batch from OPEN → PICKING when labels first printed
        // Skip if already PICKING or beyond (reprints don't change status)
        if (!batchData.labelsPrinted) {
          updateBatchStatus.mutate({
            batchId,
            status: 'PICKING',
            locationId: batchData.locationId,
          });

          // 'none' mode: dialog triggers when all picklist checkboxes are ticked
          // 'required' mode: dialog triggers via useEffect when all items scanned
        }
      }
      // Close reprint dialog if open
      setShowReprintDialog(false);
    },
  });

  // The garment labels (a QR code + a barcode each) mount ONLY for the print
  // itself. A big batch carries thousands of them, and keeping that sheet
  // mounted hidden at all times re-rendered every label on every refetch.
  //   idle      nothing mounted
  //   preparing the busy button paints first, because mounting thousands of
  //             labels can hold the main thread for seconds
  //   mounted   the labels render; the effect after that commit hands the
  //             sheet to printLabels and drops back to idle (unmounting it)
  const [labelPrintPhase, setLabelPrintPhase] = useState<'idle' | 'preparing' | 'mounted'>('idle');
  const isLabelPrintBusy = isPrintingLabels || labelPrintPhase !== 'idle';

  const {
    printRef: picklistPrintRef,
    printLabels: printPicklist,
    isPrinting: isPrintingPicklist,
  } = usePrintLabels({
    showSuccessToast: false,
    onPrintComplete: () => {
      if (batchId) {
        logPrintEvent.mutate({
          batchId,
          printType: 'PICKLIST',
        });
      }
    },
  });

  // "Print buy list" prints ONLY the Buy first section. No onPrintComplete on
  // purpose: the print-event enum has no buy-list value, and logging PICKLIST
  // would flip picklist_printed for a pick list nobody printed.
  const {
    printRef: buyListPrintRef,
    printLabels: printBuyListSheet,
    isPrinting: isPrintingBuyList,
  } = usePrintLabels({ showSuccessToast: false });

  // Invoice hooks (BLU-328)
  const invoiceEnabled = useLocationInvoiceEnabled(selectedLocation?.id);
  const trpcUtils = trpc.useUtils();
  // Eagerly fetch invoice data when invoices are enabled so invoicesPrinted flag is available.
  // Print only triggers when invoiceFetchEnabled is set (user clicked button).
  const { data: invoiceData, isFetching: isLoadingInvoices } = useInvoiceBatchData(
    batchId,
    invoiceEnabled && !!batchId
  );
  const {
    printRef: invoicePrintRef,
    printInvoices,
    isPrinting: isPrintingInvoices,
  } = usePrintInvoices({
    onPrintComplete: () => {
      if (batchId) {
        logPrintEvent.mutate({
          batchId,
          printType: 'INVOICE_BATCH',
        });
        // Invalidate invoice data cache so invoicesPrinted flag updates
        trpcUtils.productionOrders.getInvoiceBatchData.invalidate({ batchId });
      }
      setShowReprintInvoicesDialog(false);
      setInvoiceFetchEnabled(false);
    },
  });

  // Auto-print invoices when data arrives
  useEffect(() => {
    if (!invoiceData || !invoiceFetchEnabled || isPrintingInvoices) return;
    // Small delay to let React render the hidden invoice components
    const timer = setTimeout(() => {
      const date = new Date().toISOString().split('T')[0];
      const batchNum = batchData?.batchNumber ?? 'Unknown';
      printInvoices(`Batch-${batchNum}-Invoices-${date}`);
    }, 100);
    return () => clearTimeout(timer);
  }, [invoiceData, invoiceFetchEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePrintInvoices = () => {
    if (invoiceData?.invoicesPrinted) {
      setShowReprintInvoicesDialog(true);
    } else {
      setInvoiceFetchEnabled(true);
    }
  };

  const handleConfirmReprintInvoices = () => {
    setInvoiceFetchEnabled(true);
  };

  const formatPrintFilename = (type: 'Labels' | 'Picklist' | 'Buy-List') => {
    const date = new Date().toISOString().split('T')[0];
    const batchNum = batchData?.batchNumber ?? 'Unknown';
    const batchName = batchWithProgress?.name;
    const namePart = batchName ? `-${batchName.replace(/[^a-zA-Z0-9-_ ]/g, '').trim()}` : '';
    return `Batch-${batchNum}${namePart}-${type}-${date}`;
  };

  // Every label print (first print and confirmed reprint) starts here. A
  // second request while one is mounting or printing is ignored.
  const requestLabelPrint = () => {
    if (isLabelPrintBusy) return;
    setLabelPrintPhase('preparing');
  };

  // Handle print labels - show confirmation if already printed
  const handlePrintLabels = () => {
    // BLU-502: never let a print fire on the wrong order — wait for the
    // sort config, and refuse outright when it errored (the screen falls
    // back to the default order for DISPLAY, but printing that fallback
    // would physically mis-order the label stack for a configured location).
    if (sortLoading || sortError) {
      toast.info(t('batch.detail.toastSortConfigLoading'));
      return;
    }
    if (labelSizeLoading || labelTemplatesLoading) {
      toast.info(t('batch.detail.toastLabelSizeLoading'));
      return;
    }
    if (batchData?.labelsPrinted) {
      // Show confirmation dialog instead of immediate print
      setShowReprintDialog(true);
    } else {
      // First time printing - proceed immediately
      requestLabelPrint();
    }
  };

  // Handle confirmed reprint
  const handleConfirmReprint = () => {
    requestLabelPrint();
  };

  useEffect(() => {
    if (labelPrintPhase === 'preparing') {
      // One frame, then one task: the busy state is on screen before the
      // label mount occupies the main thread.
      let timer: ReturnType<typeof setTimeout> | undefined;
      const frame = requestAnimationFrame(() => {
        timer = setTimeout(() => setLabelPrintPhase('mounted'), 0);
      });
      return () => {
        cancelAnimationFrame(frame);
        if (timer !== undefined) clearTimeout(timer);
      };
    }
    if (labelPrintPhase === 'mounted') {
      // This runs after the commit that mounted every label, and children
      // finish their own mount work before a parent effect runs: QRCodeSVG
      // renders its SVG synchronously and react-barcode draws its bars in
      // componentDidMount. So the sheet's innerHTML is complete here.
      // printLabels copies it into a string synchronously, which is what lets
      // the sheet unmount on the very next render.
      printLabels('', defaultLabelSize, formatPrintFilename('Labels'));
      setLabelPrintPhase('idle');
    }
    return undefined;
    // Only the phase drives this. printLabels is stable, and the label size
    // and filename are read from the render that committed the labels.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelPrintPhase]);

  // Handle print picklist
  // NOTE: @page in customStyles overrides the default thermal label @page via CSS cascade
  const handlePrintPicklist = () => {
    // BLU-502: same wrong-order guard as handlePrintLabels.
    if (sortLoading || sortError) {
      toast.info(t('batch.detail.toastSortConfigLoading'));
      return;
    }
    // The sheet ends with Buy first; a print fired before the buy list
    // arrives would leave the blanks to buy off the paper.
    if (buyListLoading) {
      toast.info(t('batch.buyFirst.toastLoading'));
      return;
    }
    printPicklist(`
      ${A4_SHEET_CSS}
      ${BUY_FIRST_PRINT_CSS}
      @media print {
        /* Band/section header rows must never land as the last line on a
           page (paired legacy+modern properties per qr-print-utils). */
        tr.picklist-band {
          page-break-inside: avoid; break-inside: avoid;
          page-break-after: avoid; break-after: avoid;
        }
      }
    `, undefined, formatPrintFilename('Picklist'));
  };

  // "Print buy list": the Buy first section on its own A4 sheet, nothing else.
  const handlePrintBuyList = () => {
    printBuyListSheet(A4_SHEET_CSS, undefined, formatPrintFilename('Buy-List'));
  };

  // "Update buy list" (coordinator tier): the same sync createBatch runs. The
  // card itself shows the result (the notice goes, the POs appear), so only a
  // cancelled batch or a refusal says anything.
  const handleSyncBuyList = () => {
    if (!batchId) return;
    syncBuyList.mutate(
      { batchId },
      {
        onSuccess: (result) => {
          if (result.skipped === 'batch_cancelled') toast.info(t('batch.buyFirst.batchCancelled'));
        },
        onError: (err) => {
          toast.error(t('batch.buyFirst.updateFailed'), {
            description:
              jitPoErrorKey(err) === 'BATCH_NOT_FOUND'
                ? t('batch.buyFirst.updateFailedNotFound')
                : t('batch.buyFirst.updateFailedRetry'),
            duration: Infinity,
          });
        },
      }
    );
  };

  // Memoized calculations - must be before any early returns (React hooks rules)
  // These safely handle undefined batchData by returning empty arrays

  // BLU-502: ONE sortable projection feeds BOTH the picklist grouping and the
  // label print order — the two orderings are derived from the same
  // groupPicklist output, so they cannot diverge. shopifyProductType is
  // projected to the DISPLAYED value (the exact PicklistRow render logic:
  // P&P → productName, POD → alias-resolved type, falling back to the raw
  // type while aliasData is undefined) so a productType sort key orders by
  // what the picker actually reads, and two raw types aliased to one display
  // name form a contiguous run.
  const sortableLabels = useMemo<GroupableGarment[]>(() => {
    if (!batchData) return [];
    return batchData.labels.map((l) => ({
      id: l.id,
      sku: l.sku,
      pickSku: l.pickSku,
      clientLabelled: l.clientLabelled,
      clientName: l.clientName,
      shopifySku: l.shopifySku,
      productName: l.productName,
      size: l.size,
      color: l.color,
      isRedo: l.isRedo,
      redoReason: l.redoReason,
      requiresPretreat: l.requiresPretreat,
      orderNumber: l.orderNumber,
      binLabel: l.binLabel,
      shopifyProductType:
        l.fulfillmentType === 'P&P'
          ? l.productName
          : l.shopifyProductType
            ? (productTypeAliases?.[l.shopifyProductType] ?? l.shopifyProductType)
            : l.productName,
      fulfillmentType: l.fulfillmentType,
      printTechnology: l.printTechnology,
      isPriorityInjection: l.isPriorityInjection,
      pickImageUrl: l.pickImageUrl,
    }));
  }, [batchData, productTypeAliases]);

  const labelsInPrintOrder = useMemo(
    () => getLabelsInPrintOrder(sortableLabels, pickingMode, sortConfig),
    [sortableLabels, pickingMode, sortConfig]
  );

  // id → the full label row, for the print sheet. labelsInPrintOrder carries
  // the sortable projection (display-resolved product type, no item counts),
  // so each printed label reads its raw row from here: one Map lookup per
  // label, where a find() per label was ~10^8 comparisons on a 10,000-label
  // batch. First row wins on a duplicate id, as find() did.
  const labelById = useMemo(() => {
    const byId = new Map<string, NonNullable<typeof batchData>['labels'][number]>();
    for (const l of batchData?.labels ?? []) {
      if (!byId.has(l.id)) byId.set(l.id, l);
    }
    return byId;
  }, [batchData]);

  const groupedPicklist = useMemo(
    () => groupPicklist(batchId || '', sortableLabels, sortConfig),
    [batchId, sortableLabels, sortConfig]
  );

  // Band header starts for bandByFirstKey (screen + print twins render this)
  const bandStarts = useMemo(
    () => computeBandStarts(groupedPicklist.groups, sortConfig),
    [groupedPicklist.groups, sortConfig]
  );

  // Section item counts for POD·DTG / POD·DTF / P&P headers
  const { podDtgCount, podDtfCount, papCount } = useMemo(() => {
    let dtg = 0;
    let dtf = 0;
    let pap = 0;
    for (const group of groupedPicklist.groups) {
      if (group.fulfillmentType === 'P&P') {
        pap += group.quantity;
      } else if (group.printTechnology === 'DTF') {
        dtf += group.quantity;
      } else {
        dtg += group.quantity;
      }
    }
    return { podDtgCount: dtg, podDtfCount: dtf, papCount: pap };
  }, [groupedPicklist.groups]);

  // The issue dialog offers one PILE per picklist group and ORDER SKU (issue
  // matching keys on the order SKU), in picklist order. Own stock and the 3PL's
  // back-up stock of the same order SKU are separate piles, and a defective
  // report sends that pile's unit ids so the right row is decremented.
  const skuOptions = useMemo(() => {
    const options = new Map<string, PickingIssueSkuOption>();
    for (const group of groupedPicklist.groups) {
      const stockType: PickingIssueSkuOption['stockType'] = group.clientLabelled
        ? 'labelled'
        : isBackupGroup(group)
          ? 'shared'
          : 'own';
      for (const id of group.garmentIds) {
        const label = labelById.get(id);
        if (!label) continue;
        const key = `${group.groupKey}\u0000${label.sku}`;
        const option = options.get(key);
        if (option) {
          option.availableCount += 1;
          option.garmentIds.push(id);
        } else {
          options.set(key, {
            key,
            sku: label.sku,
            pickSku: group.sku,
            productName: label.productName,
            availableCount: 1,
            stockType,
            clientName: group.clientName,
            garmentIds: [id],
          });
        }
      }
    }
    return [...options.values()];
  }, [groupedPicklist.groups, labelById]);

  const { totalLabels, pretreatCount, uniqueOrders } = useMemo(() => {
    if (!batchData) return { totalLabels: 0, pretreatCount: 0, uniqueOrders: 0 };
    return {
      totalLabels: batchData.labels.length,
      pretreatCount: batchData.labels.filter((l) => l.requiresPretreat).length,
      uniqueOrders: new Set(batchData.labels.map((l) => l.orderNumber)).size,
    };
  }, [batchData]);

  // Build Shopify admin orders URL filtered to this batch's orders
  const shopifyOrdersUrl = useMemo(() => {
    if (!batchData) return null;
    const slug = getShopifyStoreSlug(organization?.shopify_domain);
    if (!slug) return null;
    const orderNames = [...new Set(batchData.labels.map((l) => l.orderNumber))];
    if (orderNames.length === 0) return null;
    const query = orderNames.join(' OR ');
    return `https://admin.shopify.com/store/${encodeURIComponent(slug)}/orders?query=${encodeURIComponent(query)}`;
  }, [batchData, organization?.shopify_domain]);

  // Pretreat groups for the picking complete dialog
  const pretreatGroups = useMemo(() =>
    groupedPicklist.groups
      .filter((g) => g.requiresPretreat)
      .map((g) => ({
        sku: g.sku,
        shopifyProductType: g.shopifyProductType,
        size: g.size,
        color: g.color,
        quantity: g.quantity,
        fulfillmentType: g.fulfillmentType,
        clientLabelled: g.clientLabelled,
        clientName: g.clientName,
      })),
    [groupedPicklist.groups]
  );
  // BLU-635: split the non-pretreat picked items by their next destination.
  // POD garments continue to the print queue; pick-and-pack (P&P / PAP) finished
  // goods go straight to packing (no print step). Pretreat items are counted
  // separately (pretreatCount) and listed in pretreatGroups, so the three
  // buckets sum to totalLabels.
  const { printingItemCount, packingItemCount } = useMemo(() => {
    if (!batchData) return { printingItemCount: 0, packingItemCount: 0 };
    let printing = 0;
    let packing = 0;
    for (const label of batchData.labels) {
      if (label.requiresPretreat) continue;
      if (label.fulfillmentType === 'P&P') {
        packing += 1;
      } else {
        printing += 1;
      }
    }
    return { printingItemCount: printing, packingItemCount: packing };
  }, [batchData]);

  // Handle picking verification via QR scan
  const handleVerifyPick = useCallback(async (qrCode: string) => {
    if (!batchId || !selectedLocation) {
      throw new Error('Batch or location not available');
    }
    const result = await verifyPickMutation.mutateAsync(
      buildVerifyPickInput(qrCode, batchId, selectedLocation.id)
    );
    return result;
  }, [batchId, selectedLocation, verifyPickMutation]);

  // Lookup garment ID -> pick SKU + fulfillment type + print tech from label
  // data (for pick progress key matching — pickProgress only carries
  // sku/size/id, and the group key follows the SKU the picker takes).
  const fulfillmentTypeByGarmentId = useMemo(() => {
    const map = new Map<string, { pickSku: string | null; ft: string; tech: string | null }>();
    if (!batchData?.labels) return map;
    for (const l of batchData.labels) {
      map.set(l.id, { pickSku: l.pickSku ?? null, ft: l.fulfillmentType || 'POD', tech: l.printTechnology ?? null });
    }
    return map;
  }, [batchData?.labels]);

  // Build a map of group key -> picked count from pick progress data.
  // MUST build the key through buildPicklistGroupKey (the single key
  // authority) — an inline template here would desync on any key change.
  const pickedCountBySkuSize = useMemo(() => {
    const counts = new Map<string, number>();
    if (!pickProgress?.items) return counts;

    for (const item of pickProgress.items) {
      if (item.picked) {
        const meta = fulfillmentTypeByGarmentId.get(item.garment_unit_id);
        const key = buildPicklistGroupKey(meta?.pickSku ?? item.sku, item.size, meta?.ft, meta?.tech);
        const current = counts.get(key) || 0;
        counts.set(key, current + 1);
      }
    }
    return counts;
  }, [pickProgress?.items, fulfillmentTypeByGarmentId]);

  // Combined checked state: local UI checks + server-confirmed picks
  const checkedGroups = useMemo(() => {
    const merged = new Set(localCheckedGroups);
    // Add server-confirmed picks (these can't be unchecked)
    for (const group of groupedPicklist.groups) {
      const pickedCount = pickedCountBySkuSize.get(group.groupKey) || 0;
      if (pickedCount >= group.quantity) {
        merged.add(group.groupKey);
      }
    }
    return merged;
  }, [localCheckedGroups, groupedPicklist.groups, pickedCountBySkuSize]);

  // Helper: check if a group is server-confirmed (not just locally checked)
  const isServerConfirmed = useCallback((groupKey: string) => {
    const group = groupedPicklist.groups.find(g => g.groupKey === groupKey);
    if (!group) return false;
    return (pickedCountBySkuSize.get(groupKey) || 0) >= group.quantity;
  }, [groupedPicklist.groups, pickedCountBySkuSize]);

  // Toggle picklist checkbox: local-only state, toggleable (unless server-confirmed)
  const togglePicklistCheck = useCallback((groupKey: string) => {
    if (isServerConfirmed(groupKey)) return;

    setLocalCheckedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, [isServerConfirmed]);

  // Auto-show picking complete dialog when all items picked ('required' mode)
  // Skip if batch is already past handover (PICKED/PACKING/CATCHING/COMPLETED)
  useEffect(() => {
    if (pickingCompleteShownRef.current) return;
    // M-5: Don't show dialog if batch query failed (status unknown)
    if (batchError) return;
    // Don't show dialog while batch status is still loading (prevents race condition)
    if (!batchWithProgress) return;

    // Skip dialog for batches that have already been handed over
    const batchStatus = batchWithProgress.status;
    if (batchStatus === 'PICKED' || batchStatus === 'PACKING' || batchStatus === 'COMPLETED' || batchStatus === 'CATCHING') return;

    const total = pickProgress?.totalItems ?? 0;
    const picked = pickProgress?.pickedItems ?? 0;
    if (isVerificationRequired && total > 0 && picked === total) {
      pickingCompleteShownRef.current = true;
      setShowPickingCompleteDialog(true);
    }
  }, [isVerificationRequired, pickProgress?.totalItems, pickProgress?.pickedItems, batchWithProgress?.status, batchError]);

  // BLU-747: 'none' mode has no picking-confirmation step. Garments stay
  // LABEL_PRINTED, which every downstream station (film prepare, matching, press,
  // DTG print, catching, packing) accepts as PICKED-equivalent, and
  // determineBatchStatus is MIN-phase based so batches still advance without it.
  // The hand-off dialog is therefore 'required'-mode only (see the effect above).

  // Loading state with aria-live for screen readers
  if (isLoading) {
    return (
      <div className="p-6 space-y-6" role="status" aria-live="polite" aria-busy="true">
        <span className="sr-only">{t('batch.detail.loadingBatch')}</span>
        <Skeleton className="h-8 w-48" aria-hidden="true" />
        <Skeleton className="h-64 w-full" aria-hidden="true" />
      </div>
    );
  }

  // Error state - ThreadHeads .empty-state pattern with aria-live
  if (error || !batchData) {
    return (
      <div className="p-6" role="alert" aria-live="assertive">
        <div className="empty-state">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" aria-hidden="true" />
          <h3 className="text-lg font-semibold mb-2">{t('batch.detail.notFound')}</h3>
          <p className="text-muted-foreground mb-4">
            {error?.message || t('batch.detail.loadError')}
          </p>
          <Button
            variant="outline"
            onClick={() => navigate(backUrl)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
            {t('batch.detail.backToBatches')}
          </Button>
        </div>
      </div>
    );
  }

  // Handle picking issue submission (defective or unavailable)
  const handlePickingIssueSubmit = (
    issueType: 'defective' | 'unavailable',
    sku: string,
    quantity: number,
    notes?: string,
    garmentUnitIds?: string[]
  ) => {
    if (!batchId || !batchData) return;

    reportPickingIssue.mutate(
      {
        issueType,
        batchId,
        sku,
        quantity,
        locationId: batchData.locationId,
        notes,
        ...(garmentUnitIds && garmentUnitIds.length > 0 ? { garmentUnitIds } : {}),
      },
      {
        onSuccess: (result) => {
          setShowInventoryErrorDialog(false);
          if (result.issueType === 'defective') {
            toast.success(
              t('batch.detail.toastDefective', { quantity, sku })
            );
          } else {
            toast.success(t('batch.detail.toastStockIssue'));
          }
        },
        onError: (error) => {
          toast.error(t('batch.detail.toastIssueFailed', { message: error.message }));
        },
      }
    );
  };

  return (
    <div className="container mx-auto px-6 py-6 max-w-7xl space-y-6 animate-enter" data-testid="batch-detail-screen">
      {/* Header - Dashboard style with icon box */}
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          {/* Back button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(backUrl)}
            aria-label={t('batch.detail.backAriaLabel')}
            data-testid="back-to-batches"
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          {/* Icon box like dashboard */}
          <div className="p-2 rounded-lg bg-primary/10">
            <LayoutGrid className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold" data-testid="batch-number">
              {t('batch.detail.title', { number: batchData.batchNumber })}
            </h1>
            <p className="text-muted-foreground">
              {t('batch.detail.itemsAcrossOrders', { items: totalLabels, orders: uniqueOrders })}
              {shopifyOrdersUrl && (
                <a
                  href={shopifyOrdersUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex ml-1.5 align-text-top text-muted-foreground/60 hover:text-foreground transition-colors"
                  aria-label={t('batch.detail.viewInShopify')}
                  title={t('batch.detail.viewInShopify')}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </p>
          </div>
        </div>

        {/* Print buttons - below header, left-aligned */}
        {/* size="lg" for warehouse touch targets (min 44px) */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <Button
            variant="outline"
            size="lg"
            onClick={handlePrintPicklist}
            disabled={isPrintingPicklist || sortLoading || sortError || buyListLoading}
            aria-label={t('batch.detail.printPicklistAria', { number: batchData.batchNumber })}
            data-testid="print-picklist-btn"
            className="gap-2 h-14"
          >
            {isPrintingPicklist ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            {isPrintingPicklist ? t('batch.detail.printing') : t('batch.detail.printPicklist')}
          </Button>
          {invoiceEnabled && (
            <Button
              variant="outline"
              size="lg"
              onClick={handlePrintInvoices}
              disabled={isPrintingInvoices || isLoadingInvoices}
              className="gap-2 h-14"
              aria-label={t('invoice.printInvoicesAria', { number: batchData.batchNumber })}
              data-testid="print-invoices-btn"
            >
              {isPrintingInvoices || isLoadingInvoices ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Receipt className="h-4 w-4" />
              )}
              {isPrintingInvoices ? t('invoice.printing') : isLoadingInvoices ? t('invoice.loading') : t('invoice.printInvoices')}
              {!isPrintingInvoices && !isLoadingInvoices && invoiceData?.invoicesPrinted && (
                <Check className="h-4 w-4 text-green-500" />
              )}
            </Button>
          )}
          <Button
            size="lg"
            onClick={handlePrintLabels}
            disabled={isLabelPrintBusy || labelSizeLoading || labelTemplatesLoading || sortLoading || sortError}
            className="gap-2 h-14"
            aria-label={batchData.labelsPrinted ? t('batch.detail.printLabelsReprintAria', { count: totalLabels, number: batchData.batchNumber }) : t('batch.detail.printLabelsAria', { count: totalLabels, number: batchData.batchNumber })}
            data-testid="print-labels-btn"
          >
            {isLabelPrintBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            {isLabelPrintBusy ? t('batch.detail.printing') : t('batch.detail.printLabels')}
            {!isLabelPrintBusy && batchData.labelsPrinted && (
              <Check className="h-4 w-4 text-green-500" />
            )}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowInventoryErrorDialog(true)}
            className="text-amber-600 hover:text-amber-700 gap-2 h-14"
            aria-label={t('batch.detail.reportIssueAria')}
            data-testid="report-picking-issue-btn"
          >
            <AlertCircle className="h-4 w-4" />
            {t('batch.detail.reportIssue')}
          </Button>
          {/* Outside every print template, so it never prints (print:hidden is belt and braces). */}
          {hasJustInTimePos && (
            <BuyFirstMenu
              batchNumber={batchData.batchNumber}
              onPrintBuyList={handlePrintBuyList}
              isPrinting={isPrintingBuyList}
            />
          )}
        </div>
      </div>

      {/* Picking Scanner Section - shown when verification required and scanner enabled */}
      {isVerificationRequired && prodSettings.showPickingScanner && (
        <PickingScannerSection
          onVerify={handleVerifyPick}
          isLoading={verifyPickMutation.isPending}
          disabled={!batchId || !selectedLocation}
          locationId={selectedLocation?.id ?? ''}
          batchId={batchId ?? ''}
        />
      )}

      {/* Batch Progress Section — collapsed by default */}
      {batchWithProgress?.progress && (
        <Card>
          <button
            type="button"
            className="w-full flex items-center justify-between px-6 py-3 text-left"
            onClick={() => setProgressExpanded(prev => !prev)}
            aria-expanded={progressExpanded}
          >
            <CardTitle className="text-base">{t('batch.detail.workflowProgress')}</CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('batch.detail.percentComplete', { percent: batchWithProgress.progress.completionPercent })}
              </span>
              <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${progressExpanded ? 'rotate-180' : ''}`} />
            </div>
          </button>
          {progressExpanded && (
            <CardContent className="space-y-4 pt-0">
              {/* Progress bar */}
              <BatchProgressBar
                progress={batchWithProgress.progress}
                showCompletionPercent={false}
              />

              {/* Stage cards grid */}
              <BatchStageCards progress={batchWithProgress.progress} />
            </CardContent>
          )}
        </Card>
      )}

      {/* Print status - Dashboard style success banner */}
      {batchData.labelsPrinted && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-[hsl(var(--production-accent))]/30 bg-[hsl(var(--metric-total))]">
          <div className="p-1.5 rounded-lg bg-[hsl(var(--production-accent))]/20">
            <Check className="h-4 w-4 text-[hsl(var(--production-accent))]" />
          </div>
          <span className="text-sm font-medium text-[hsl(160,60%,25%)] dark:text-[hsl(160,60%,70%)]">
            {t('batch.detail.labelsPrinted')}{' '}
            {batchData.labelsPrintedAt &&
              new Date(batchData.labelsPrintedAt).toLocaleString()}
          </span>
        </div>
      )}

      {/* Pick Progress Header - derived from checkbox state.
          BLU-747: 'required' mode only — in 'none' mode nothing marks garments
          PICKED, so this would sit at 0/N for the life of the batch. */}
      {isVerificationRequired && groupedPicklist.groups.length > 0 && (
        <PickProgressHeader
          pickedCount={groupedPicklist.groups.reduce(
            (sum, g) => sum + (checkedGroups.has(g.groupKey) ? g.quantity : 0),
            0
          )}
          totalCount={groupedPicklist.totalItems}
          className="border rounded-lg bg-card"
        />
      )}

      {/* Picklist - Single flat table, sorted by SKU then size */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t('batch.detail.picklist', { count: groupedPicklist.totalItems })}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead className="bg-muted">
                <tr>
                  {/* BLU-747: select-all is 'required'-mode only. In 'none' mode the
                      picklist is a read-only pick sheet with no checkbox column, so
                      this header cell is dropped and the bands span one less. */}
                  {isVerificationRequired && (
                  <th className="px-4 py-3 text-center text-sm font-medium w-12">
                    <Checkbox
                      checked={
                        groupedPicklist.groups.length > 0 && checkedGroups.size > 0
                          ? checkedGroups.size === groupedPicklist.groups.length
                            ? true
                            : 'indeterminate'
                          : false
                      }
                      disabled={groupedPicklist.groups.every(g => isServerConfirmed(g.groupKey))}
                      onCheckedChange={(checked) => {
                        if (isVerificationRequired) {
                          if (checked) {
                            // QR mode: bulk-verify all unpicked items
                            if (!batchId || !selectedLocation?.id) return;
                            const unpickedIds = groupedPicklist.groups
                              .filter(g => !checkedGroups.has(g.groupKey))
                              .flatMap(g => g.garmentIds);
                            if (unpickedIds.length === 0) return;
                            verifyGroupMutation.mutate({
                              garmentUnitIds: unpickedIds,
                              batchId,
                              locationId: selectedLocation.id,
                            });
                          } else {
                            // QR mode uncheck: clear local checks (server-confirmed stay)
                            setLocalCheckedGroups(prev => {
                              const next = new Set(prev);
                              for (const group of groupedPicklist.groups) {
                                if (!isServerConfirmed(group.groupKey)) {
                                  next.delete(group.groupKey);
                                }
                              }
                              return next;
                            });
                          }
                        } else {
                          // None mode: toggle all local checkboxes
                          if (checked) {
                            setLocalCheckedGroups(prev => {
                              const next = new Set(prev);
                              for (const group of groupedPicklist.groups) {
                                next.add(group.groupKey);
                              }
                              return next;
                            });
                          } else {
                            setLocalCheckedGroups(prev => {
                              const next = new Set(prev);
                              for (const group of groupedPicklist.groups) {
                                if (!isServerConfirmed(group.groupKey)) {
                                  next.delete(group.groupKey);
                                }
                              }
                              return next;
                            });
                          }
                        }
                      }}
                      className="h-5 w-5"
                      aria-label={
                        checkedGroups.size === groupedPicklist.groups.length
                          ? t('batch.detail.uncheckAll')
                          : t('batch.detail.checkAll')
                      }
                    />
                  </th>
                  )}
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('batch.detail.colQty')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('batch.detail.colTypeProduct')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('batch.detail.colSize')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('batch.detail.colColor')}</th>
                  <th className="px-4 py-3 text-left text-sm font-medium">{t('batch.detail.colSku')}</th>
                </tr>
              </thead>
              <tbody>
                {groupedPicklist.groups.map((group, index) => {
                  const prevGroup = index > 0 ? groupedPicklist.groups[index - 1] : null;
                  // BLU-502: transition detection, no index special-case —
                  // the old `index === 0` positional check held only because
                  // P&P sorted last; it breaks when fulfillmentDir is 'desc'.
                  // DTF split: POD now sections by print technology too
                  // (POD·DTG purple / POD·DTF fuchsia / PAP sky).
                  const section = sectionOfGroup(group);
                  const prevSection = prevGroup ? sectionOfGroup(prevGroup) : null;
                  const showPodDtgHeader = section === 'DTG' && prevSection !== 'DTG';
                  const showPodDtfHeader = section === 'DTF' && prevSection !== 'DTF';
                  const showPapHeader = section === 'PAP' && prevSection !== 'PAP';
                  const band = bandStarts.get(index);

                  const sectionHeader = (
                    <>
                      {showPodDtgHeader && (
                        <tr key="section-pod-dtg" className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/40 dark:to-indigo-950/40 border-y-2 border-purple-200 dark:border-purple-800">
                          <td colSpan={picklistColSpan} className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/60 border border-purple-200 dark:border-purple-700">
                                <Printer className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-base font-bold tracking-tight text-purple-900 dark:text-purple-100">
                                  {t('batch.detail.podDtgSection')}
                                </span>
                                <span className="text-sm text-purple-600/80 dark:text-purple-400/80 hidden sm:inline">
                                  {t('batch.detail.podDtgSectionDesc')}
                                </span>
                              </div>
                              <span className="ml-auto text-sm font-semibold tabular-nums text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/60 px-2.5 py-0.5 rounded-full border border-purple-200 dark:border-purple-700">
                                {t('batch.detail.itemCount', { count: podDtgCount })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {showPodDtfHeader && (
                        <tr key="section-pod-dtf" className="bg-fuchsia-50 dark:bg-fuchsia-950/40 border-y-2 border-fuchsia-200 dark:border-fuchsia-800">
                          <td colSpan={picklistColSpan} className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-fuchsia-100 dark:bg-fuchsia-900/60 border border-fuchsia-200 dark:border-fuchsia-700">
                                <Film className="w-4 h-4 text-fuchsia-600 dark:text-fuchsia-400" />
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-base font-bold tracking-tight text-fuchsia-900 dark:text-fuchsia-100">
                                  {t('batch.detail.podDtfSection')}
                                </span>
                                <span className="text-sm text-fuchsia-600/80 dark:text-fuchsia-400/80 hidden sm:inline">
                                  {t('batch.detail.podDtfSectionDesc')}
                                </span>
                              </div>
                              <span className="ml-auto text-sm font-semibold tabular-nums text-fuchsia-700 dark:text-fuchsia-300 bg-fuchsia-100 dark:bg-fuchsia-900/60 px-2.5 py-0.5 rounded-full border border-fuchsia-200 dark:border-fuchsia-700">
                                {t('batch.detail.itemCount', { count: podDtfCount })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {showPapHeader && (
                        <tr key="section-pap" className="bg-gradient-to-r from-sky-50 to-teal-50 dark:from-sky-950/40 dark:to-teal-950/40 border-y-2 border-sky-200 dark:border-sky-800">
                          <td colSpan={picklistColSpan} className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/60 border border-sky-200 dark:border-sky-700">
                                <Package className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                              </div>
                              <div className="flex items-baseline gap-2">
                                <span className="text-base font-bold tracking-tight text-sky-900 dark:text-sky-100">
                                  {t('batch.detail.papSection')}
                                </span>
                                <span className="text-sm text-sky-600/80 dark:text-sky-400/80 hidden sm:inline">
                                  {t('batch.detail.papSectionDesc')}
                                </span>
                              </div>
                              <span className="ml-auto text-sm font-semibold tabular-nums text-sky-700 dark:text-sky-300 bg-sky-100 dark:bg-sky-900/60 px-2.5 py-0.5 rounded-full border border-sky-200 dark:border-sky-700">
                                {t('batch.detail.itemCount', { count: papCount })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                      {/* BLU-502 band header (bandByFirstKey). ⚠ KEEP-IN-LOCKSTEP
                          with the PRINT band row twin below (inline styles) —
                          both render from computeBandStarts. */}
                      {band && (
                        <tr
                          key={`band-${group.groupKey}`}
                          className="bg-muted/60 border-y border-border"
                        >
                          <td colSpan={picklistColSpan} className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold">
                                {band.label || '-'}
                              </span>
                              <span className="ml-auto text-xs font-medium tabular-nums text-muted-foreground">
                                {t('batch.detail.itemCount', { count: band.count })}
                              </span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );

                  if (isVerificationRequired) {
                    return (
                      <PicklistRowWithDivider key={group.groupKey} divider={sectionHeader}>
                        <PicklistRow
                          group={group}
                          pickedCount={pickedCountBySkuSize.get(group.groupKey) || 0}
                          isEven={index % 2 === 0}
                          productTypeAliases={productTypeAliases}
                          isChecked={checkedGroups.has(group.groupKey)}
                          onToggle={() => togglePicklistCheck(group.groupKey)}
                        />
                      </PicklistRowWithDivider>
                    );
                  }

                  // BLU-747: 'none' mode — read-only pick sheet. No checkbox column,
                  // no click target, no confirmed/checked tinting: nothing in this mode
                  // marks a garment PICKED, and no downstream station needs it to be.
                  const displayName = picklistDisplayName(group, productTypeAliases);
                  return (
                    <PicklistRowWithDivider key={group.groupKey} divider={sectionHeader}>
                      <tr className={index % 2 === 0 ? 'h-16 bg-card' : 'h-16 bg-muted/30'}>
                        <td className="px-4 py-4 text-base font-semibold">
                          {group.quantity}
                        </td>
                        {/* py-2, not py-4: the 48px pick photo plus 16px of
                            padding keeps this row at h-16. Twin of PicklistRow's
                            product cell (duplicated row markup). */}
                        <td className="px-4 py-2 text-base">
                          <div className="flex items-center gap-3">
                            <PicklistPickImage
                              pickImageUrl={group.pickImageUrl}
                              displayName={displayName}
                              size={group.size}
                              sku={group.sku}
                            />
                            <span className="inline-flex items-center gap-2">
                              {displayName}
                              {group.redoCount > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                                  <RotateCcw className="h-3 w-3" aria-hidden="true" />
                                  {t('batch.detail.redo', { count: group.redoCount })}
                                </span>
                              )}
                              {group.orderChangeCount > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-400 px-2 py-0.5 text-xs font-medium text-amber-700 dark:border-amber-600 dark:text-amber-300">
                                  <Plus className="h-3 w-3" aria-hidden="true" />
                                  {t('batch.detail.orderChange', { count: group.orderChangeCount })}
                                </span>
                              )}
                              {group.priorityInjectionCount > 0 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-xs font-medium text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">
                                  <ArrowUpCircle className="h-3 w-3" aria-hidden="true" />
                                  {t('batch.detail.requeued', { count: group.priorityInjectionCount })}
                                </span>
                              )}
                              {/* DTF chip — twin of PicklistRow's (duplicated row markup). */}
                              {group.fulfillmentType !== 'P&P' && group.printTechnology === 'DTF' && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-100 px-2 py-0.5 text-xs font-medium text-fuchsia-700 dark:bg-fuchsia-900/30 dark:text-fuchsia-300">
                                  {t('picking.badges.dtf')}
                                </span>
                              )}
                              {/* Client-labelled badge — twin of PicklistRow's. */}
                              {group.clientLabelled && <ClientLabelledBadge clientName={group.clientName} />}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-xl font-bold">{group.size}</td>
                        <td className="px-4 py-4 text-base">{group.color}</td>
                        <td className={isBackupGroup(group) ? 'px-4 py-4 text-base font-mono font-bold' : 'px-4 py-4 text-base font-mono'}>{group.sku}</td>
                      </tr>
                    </PicklistRowWithDivider>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Summary footer */}
          <div className="px-4 py-3 border-t bg-muted/50 text-sm text-muted-foreground">
            {t('batch.detail.totalSummary', { items: groupedPicklist.totalItems, groups: groupedPicklist.summary.totalGroups })}
            {podDtgCount > 0 && (
              <span className="ml-2 text-purple-600 dark:text-purple-400">
                | {t('batch.detail.podDtgSummary', { count: podDtgCount })}
              </span>
            )}
            {podDtfCount > 0 && (
              <span className="ml-2 text-fuchsia-600 dark:text-fuchsia-400">
                | {t('batch.detail.podDtfSummary', { count: podDtfCount })}
              </span>
            )}
            {papCount > 0 && (
              <span className="ml-2 text-sky-600 dark:text-sky-400">
                | {t('batch.detail.papSummary', { count: papCount })}
              </span>
            )}
            {groupedPicklist.summary.totalRedoCount > 0 && (
              <span className="ml-2 text-destructive">
                | {t('batch.detail.redosSummary', { count: groupedPicklist.summary.totalRedoCount })}
              </span>
            )}
            {groupedPicklist.summary.totalOrderChangeCount > 0 && (
              <span className="ml-2 text-amber-700 dark:text-amber-300">
                | {t('batch.detail.orderChangesSummary', { count: groupedPicklist.summary.totalOrderChangeCount })}
              </span>
            )}
          </div>
        </CardContent>

      </Card>

      {/* Buy first: the just-in-time blanks to buy for this batch. Nothing at
          all for a batch without any. ⚠ KEEP-IN-LOCKSTEP with the print twin
          (BuyFirstPrintSection) at the end of the picklist sheet below. */}
      {buyListError ? (
        <BuyFirstLoadError onRetry={() => void refetchBuyList()} retrying={buyListRefetching} />
      ) : (
        hasBuyFirstContent(buyList) && (
          <BuyFirstCard
            buyList={buyList}
            canUpdate={isCoordinatorRole(role)}
            canOpenPurchaseOrders={can('purchase_orders.view')}
            onUpdate={handleSyncBuyList}
            isUpdating={syncBuyList.isPending}
          />
        )
      )}

      {/* Hidden print template for labels. The container is always mounted
          (it owns labelPrintRef); the labels inside it exist only during the
          'mounted' print phase. */}
      <div
        ref={labelPrintRef}
        style={{ display: 'none' }}
        role="region"
        aria-label={t('batch.detail.labelPrintTemplateAria', { count: totalLabels, number: batchData.batchNumber })}
      >
        {labelPrintPhase === 'mounted' && labelsInPrintOrder.map((sortedLabel) => {
          const originalLabel = labelById.get(sortedLabel.id);
          if (!originalLabel) return null;
          return (
            <LabelErrorBoundary key={sortedLabel.id} garmentId={sortedLabel.id}>
              <GarmentLabel
                garment={{
                  id: originalLabel.id,
                  orderNumber: originalLabel.orderNumber,
                  sku: originalLabel.sku,
                  shopifySku: originalLabel.shopifySku,
                  productName: originalLabel.productName,
                  size: originalLabel.size,
                  color: originalLabel.color,
                  requiresPretreat: originalLabel.requiresPretreat,
                  fulfillmentType: originalLabel.fulfillmentType,
                  itemNumber: originalLabel.itemNumber,
                  totalItems: originalLabel.totalItems,
                  shopifyProductType: originalLabel.shopifyProductType,
                  // BLU-233: real per-garment tech → label QR m + DTF/DTG indicator.
                  printTechnology: originalLabel.printTechnology,
                  // Phase 104 (D-09): manual order print areas ([] otherwise).
                  printArea: originalLabel.printArea,
                }}
                binLabel={originalLabel.binLabel || undefined}
                isRedo={originalLabel.isRedo}
                redoReason={originalLabel.redoReason || undefined}
              />
            </LabelErrorBoundary>
          );
        })}
      </div>

      {/* Hidden print template for picklist (A4 grouped format) */}
      <div
        ref={picklistPrintRef}
        style={{ display: 'none' }}
        role="region"
        aria-label={t('batch.detail.printTemplateAria', { number: batchData.batchNumber })}
      >
        {/* Note: className="label" required for usePrintLabels validation */}
        <div className="label" style={{ fontFamily: '"PingFang SC", "Microsoft YaHei", "Noto Sans SC", Arial, sans-serif', padding: '20px' }}>
          <h1 style={{ fontSize: '24px', marginBottom: '10px' }}>
            {t('batch.detail.printTitle', { number: batchData.batchNumber })}
          </h1>
          <p style={{ marginBottom: '20px', color: '#666' }}>
            {t('batch.detail.printSummary', { items: groupedPicklist.totalItems, groups: groupedPicklist.summary.totalGroups, orders: uniqueOrders })} | {t('batch.detail.printGenerated', { date: new Date().toLocaleString() })}
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', width: '50px' }}>
                  {t('batch.detail.colPick')}
                </th>
                <th style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', width: '60px' }}>
                  {t('batch.detail.colQty')}
                </th>
                <th style={{ border: '1px solid #000', padding: '10px', textAlign: 'left' }}>
                  {t('batch.detail.colTypeProduct')}
                </th>
                <th style={{ border: '1px solid #000', padding: '10px', textAlign: 'left' }}>
                  {t('batch.detail.colSize')}
                </th>
                <th style={{ border: '1px solid #000', padding: '10px', textAlign: 'left' }}>
                  {t('batch.detail.colColor')}
                </th>
                <th style={{ border: '1px solid #000', padding: '10px', textAlign: 'left' }}>
                  {t('batch.detail.colSku')}
                </th>
              </tr>
            </thead>
            <tbody>
              {groupedPicklist.groups.map((group, index) => {
                const prevGroup = index > 0 ? groupedPicklist.groups[index - 1] : null;
                // BLU-502: transition detection, no index special-case — see
                // the screen twin above. DTF split mirrors the screen headers.
                const printSection = sectionOfGroup(group);
                const printPrevSection = prevGroup ? sectionOfGroup(prevGroup) : null;
                const showPrintPodDtgHeader = printSection === 'DTG' && printPrevSection !== 'DTG';
                const showPrintPodDtfHeader = printSection === 'DTF' && printPrevSection !== 'DTF';
                const showPrintPapHeader = printSection === 'PAP' && printPrevSection !== 'PAP';
                const printBand = bandStarts.get(index);
                return (
                  <PicklistRowWithDivider
                    key={group.groupKey}
                    divider={
                      <>
                        {showPrintPodDtgHeader && (
                          <tr key="print-section-pod-dtg" className="picklist-band">
                            <td
                              colSpan={6}
                              style={{
                                padding: '10px 12px',
                                backgroundColor: '#f3f0ff',
                                borderTop: '2px solid #7c3aed',
                                borderBottom: '2px solid #7c3aed',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: '#4c1d95' }}>
                                  &#9113; {t('batch.detail.podDtgSection')}
                                </span>
                                <span style={{ fontSize: '12px', color: '#6d28d9' }}>
                                  {t('batch.detail.podDtgSectionDesc')}
                                </span>
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#5b21b6',
                                    backgroundColor: '#ede9fe',
                                    padding: '2px 10px',
                                    borderRadius: '10px',
                                    border: '1px solid #c4b5fd',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {t('batch.detail.itemCount', { count: podDtgCount })}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                        {showPrintPodDtfHeader && (
                          <tr key="print-section-pod-dtf" className="picklist-band">
                            <td
                              colSpan={6}
                              style={{
                                padding: '10px 12px',
                                backgroundColor: '#fdf2fa',
                                borderTop: '2px solid #c026d3',
                                borderBottom: '2px solid #c026d3',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: '#701a75' }}>
                                  &#9707; {t('batch.detail.podDtfSection')}
                                </span>
                                <span style={{ fontSize: '12px', color: '#a21caf' }}>
                                  {t('batch.detail.podDtfSectionDesc')}
                                </span>
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#86198f',
                                    backgroundColor: '#fae8ff',
                                    padding: '2px 10px',
                                    borderRadius: '10px',
                                    border: '1px solid #f0abfc',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {t('batch.detail.itemCount', { count: podDtfCount })}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                        {showPrintPapHeader && (
                          <tr key="print-section-pap" className="picklist-band">
                            <td
                              colSpan={6}
                              style={{
                                padding: '10px 12px',
                                backgroundColor: '#f0f9ff',
                                borderTop: '2px solid #0284c7',
                                borderBottom: '2px solid #0284c7',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 700, color: '#0c4a6e' }}>
                                  &#9745; {t('batch.detail.papSection')}
                                </span>
                                <span style={{ fontSize: '12px', color: '#0369a1' }}>
                                  {t('batch.detail.papSectionDesc')}
                                </span>
                                <span
                                  style={{
                                    marginLeft: 'auto',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#075985',
                                    backgroundColor: '#e0f2fe',
                                    padding: '2px 10px',
                                    borderRadius: '10px',
                                    border: '1px solid #7dd3fc',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {t('batch.detail.itemCount', { count: papCount })}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                        {/* BLU-502 band header (bandByFirstKey). ⚠ KEEP-IN-LOCKSTEP
                            with the SCREEN band row twin above (Tailwind) —
                            usePrintLabels copies innerHTML into a fresh document
                            with no Tailwind, so this twin must stay inline-styled. */}
                        {printBand && (
                          <tr key={`print-band-${group.groupKey}`} className="picklist-band">
                            <td
                              colSpan={6}
                              style={{
                                padding: '8px 12px',
                                backgroundColor: '#f5f5f5',
                                borderTop: '2px solid #000',
                                borderBottom: '1px solid #000',
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '14px', fontWeight: 700 }}>
                                  {printBand.label || '-'}
                                </span>
                                <span style={{ marginLeft: 'auto', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {t('batch.detail.itemCount', { count: printBand.count })}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    }
                  >
                    <tr>
                      <td style={{ border: '1px solid #000', padding: '10px', textAlign: 'center' }}>
                        <span style={{ fontSize: '18px' }}>&#9744;</span>
                      </td>
                      <td style={{ border: '1px solid #000', padding: '10px', textAlign: 'center', fontWeight: 'bold' }}>
                        {group.quantity}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {/* Pick photo. ⚠ KEEP-IN-LOCKSTEP with the SCREEN's
                              PicklistPickImage (same group.pickImageUrl photo, never a
                              different one). Plain inline-styled <img>: usePrintLabels copies
                              innerHTML into a fresh document with no Tailwind or React.
                              No loading="lazy": the print iframe is 0x0, so a lazy image
                              never enters the viewport and never loads. A 120px copy on
                              purpose (about 300 dpi at 40px): full-size photos made the PDF
                              slow. No fallback in print; a failed thumbnail is hidden by
                              printHtmlViaIframe's broken-image rule. */}
                          {group.pickImageUrl ? (
                            <img
                              src={thumbnailUrl(group.pickImageUrl, 120) ?? undefined}
                              alt=""
                              width={40}
                              height={40}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'contain',
                                border: '1px solid #ccc',
                                flexShrink: 0,
                                display: 'block',
                                backgroundColor: '#fff',
                              }}
                            />
                          ) : (
                            <span
                              style={{
                                width: '40px',
                                height: '40px',
                                border: '1px dashed #bbb',
                                flexShrink: 0,
                                display: 'inline-block',
                              }}
                            />
                          )}
                          <span>
                            {group.fulfillmentType === 'P&P'
                              ? group.productName
                              : (group.shopifyProductType
                                  ? (productTypeAliases?.[group.shopifyProductType] ?? group.shopifyProductType)
                                  : group.productName)}
                            {group.redoCount > 0 && (
                              <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 'bold', color: '#b45309' }}>
                                ↻ {t('batch.detail.redo', { count: group.redoCount })}
                              </span>
                            )}
                            {group.orderChangeCount > 0 && (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  color: '#92400e',
                                  border: '1px solid #92400e',
                                  borderRadius: '9999px',
                                  padding: '0 6px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                + {t('batch.detail.orderChange', { count: group.orderChangeCount })}
                              </span>
                            )}
                            {group.priorityInjectionCount > 0 && (
                              <span style={{ marginLeft: '8px', fontSize: '12px', fontWeight: 'bold', color: '#0369a1' }}>
                                ↑ {t('batch.detail.requeued', { count: group.priorityInjectionCount })}
                              </span>
                            )}
                            {/* Text inside a border, never colour alone: the
                                sheet is often printed in black and white. */}
                            {group.clientLabelled && (
                              <span
                                style={{
                                  marginLeft: '8px',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  border: '1px solid #000',
                                  borderRadius: '9999px',
                                  padding: '0 6px',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {clientLabelledLabel(t, group.clientName)}
                              </span>
                            )}
                          </span>
                        </div>
                      </td>
                      <td style={{ border: '1px solid #000', padding: '10px' }}>{group.size}</td>
                      <td style={{ border: '1px solid #000', padding: '10px' }}>{group.color}</td>
                      <td
                        style={{
                          border: '1px solid #000',
                          padding: '10px',
                          fontFamily: 'monospace',
                          ...(isBackupGroup(group) ? { fontWeight: 'bold' } : {}),
                        }}
                      >
                        {group.sku}
                      </td>
                    </tr>
                  </PicklistRowWithDivider>
                );
              })}
            </tbody>
          </table>
          <p style={{ marginTop: '20px', fontSize: '12px', color: '#666' }}>
            {t('batch.detail.totalSummary', { items: groupedPicklist.totalItems, groups: groupedPicklist.summary.totalGroups })}
            {podDtgCount > 0 && <span> | {t('batch.detail.printPodDtgSummary', { count: podDtgCount })}</span>}
            {podDtfCount > 0 && <span> | {t('batch.detail.printPodDtfSummary', { count: podDtfCount })}</span>}
            {papCount > 0 && <span> | {t('batch.detail.printPapSummary', { count: papCount })}</span>}
            {groupedPicklist.summary.totalRedoCount > 0 && (
              <span> | {t('batch.detail.redosSummary', { count: groupedPicklist.summary.totalRedoCount })}</span>
            )}
            {groupedPicklist.summary.totalOrderChangeCount > 0 && (
              <span> | {t('batch.detail.orderChangesSummary', { count: groupedPicklist.summary.totalOrderChangeCount })}</span>
            )}
          </p>
          {/* Buy first: ALWAYS the last sheet (break-before: page, inline and
              in the print stylesheet). ⚠ KEEP-IN-LOCKSTEP with BuyFirstCard. */}
          {hasBuyFirstContent(buyList) && (
            <BuyFirstPrintSection buyList={buyList} batchNumber={batchData.batchNumber} breakBefore />
          )}
        </div>
      </div>

      {/* Hidden print template for "Print buy list": the Buy first section
          only, on its own sheet (no page break before it). */}
      <div
        ref={buyListPrintRef}
        style={{ display: 'none' }}
        role="region"
        aria-label={t('batch.buyFirst.printTemplateAria', { number: batchData.batchNumber })}
      >
        {hasBuyFirstContent(buyList) && (
          <div className="label" style={{ fontFamily: '"PingFang SC", "Microsoft YaHei", "Noto Sans SC", Arial, sans-serif', padding: '20px' }}>
            <BuyFirstPrintSection buyList={buyList} batchNumber={batchData.batchNumber} />
          </div>
        )}
      </div>

      {/* Hidden print template for invoices (A4 format) */}
      {invoiceEnabled && invoiceData && (
        <div
          ref={invoicePrintRef}
          style={{ display: 'none' }}
          aria-hidden="true"
        >
          {invoiceData.orders.map((order) => (
            <OrderInvoice
              key={order.orderId}
              order={order}
              locationName={invoiceData.locationName}
              batchNumber={invoiceData.batchNumber}
            />
          ))}
        </div>
      )}

      {/* Reprint invoices confirmation dialog */}
      {invoiceData && (
        <ReprintInvoicesDialog
          open={showReprintInvoicesDialog}
          onOpenChange={setShowReprintInvoicesDialog}
          batchNumber={invoiceData.batchNumber}
          lastPrintedAt={invoiceData.invoicesPrintedAt}
          onConfirm={handleConfirmReprintInvoices}
          isConfirming={isPrintingInvoices || isLoadingInvoices}
        />
      )}

      {/* Reprint confirmation dialog */}
      <ReprintConfirmDialog
        open={showReprintDialog}
        onOpenChange={setShowReprintDialog}
        batchNumber={batchData.batchNumber}
        lastPrintedAt={
          batchData.labelsPrintedAt
            ? new Date(batchData.labelsPrintedAt).toLocaleString()
            : 'Unknown'
        }
        onConfirm={handleConfirmReprint}
        isConfirming={isLabelPrintBusy}
      />

      {/* Picking issue dialog (defective or unavailable) */}
      <PickingIssueDialog
        open={showInventoryErrorDialog}
        onOpenChange={setShowInventoryErrorDialog}
        batchNumber={batchData.batchNumber}
        skuOptions={skuOptions}
        onSubmit={handlePickingIssueSubmit}
        isSubmitting={reportPickingIssue.isPending}
      />

      {/* Picking complete confirmation dialog */}
      <PickingCompleteDialog
        open={showPickingCompleteDialog}
        onOpenChange={(open) => {
          setShowPickingCompleteDialog(open);
          if (!open) pickingCompleteShownRef.current = false;
        }}
        onHandOff={() => {
          if (batchId) {
            localStorage.removeItem(`pickChecks-${batchId}`);
            completePickingPhase.mutate(
              { batchId },
              {
                onError: (err) => {
                  toast.error(t('batch.detail.toastPickingFailed', { message: err.message }));
                },
              },
            );
          }
        }}
        batchNumber={batchData.batchNumber}
        totalItems={totalLabels}
        pretreatGroups={pretreatGroups}
        pretreatCount={pretreatCount}
        printingCount={printingItemCount}
        packingCount={packingItemCount}
      />
    </div>
  );
}

/** Renders an optional divider row before a picklist row inside <tbody> */
function PicklistRowWithDivider({ divider, children }: { divider: React.ReactNode; children: React.ReactNode }) {
  return (
    <>
      {divider}
      {children}
    </>
  );
}

export default BatchDetailScreen;
